import { objectSome } from '@web-platform/shared-universal-utils-frontend/src/helpers/helpers';
import findLast from 'lodash/findLast';
import { createSelector } from 'reselect';

import { getAllMutations } from '../state';
import { getKey } from '../state/shared/network/getKey';

import type { MutationType } from '@web-platform/shared-universal-types/src/network/requestTypes';
import type { Variables } from '@web-platform/shared-universal-types/src/network/types';
import type { MutationItem } from '@web-platform/shared-universal-types/src/redux/state/network/mutations';

// eslint-disable-next-line @skyscanner/rules/no-jira-todo
// TODO make these more efficient by having an index by key. It'll also get rid of the reliance on key order
type PropsForHasPending = {
  type: MutationType;
  variables: Variables;
};

export const makeHasPending = () =>
  createSelector(
    [getAllMutations, (_, props: PropsForHasPending) => props],
    (mutationsState, { type, variables }: PropsForHasPending) => {
      const desiredKey = getKey(type, variables);
      return objectSome(
        mutationsState,
        (value: MutationItem) =>
          getKey(value.type, value.variables) === desiredKey && (!value.complete || value.inFlight),
      );
    },
  );

type PropsForMakeGetCount = {
  type: MutationType;
  variables?: Variables;
};

export const makeGetCount = () =>
  createSelector(
    [getAllMutations, (_, props: PropsForMakeGetCount) => props],
    (mutationsState, { type, variables }) => {
      if (variables === undefined) {
        return Object.values(mutationsState).filter((value: MutationItem) => value.type === type)
          .length;
      }

      const desiredKey = getKey(type, variables);
      return Object.values(mutationsState).filter(
        (value: MutationItem) => getKey(value.type, value.variables) === desiredKey,
      ).length;
    },
  );

const doesMutationMatchType = <MT extends MutationType>(
  lastMutation: MutationItem | undefined,
  type: MT,
): lastMutation is MutationItem & { type: MT } => lastMutation?.type === type;

export const makeGetLatest = <MT extends MutationType>() =>
  createSelector(
    [getAllMutations, (_, props: { type: MT; variables: Variables }) => props],
    (mutationsState, { type, variables }) => {
      const lastMutation = findLast(
        mutationsState,
        (value) => getKey(value.type, value.variables) === getKey(type, variables),
      );

      return doesMutationMatchType(lastMutation, type) ? lastMutation : undefined;
    },
  );
